import { type UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'

import type { Language } from '@/bindings/Language'
import type { OrderStatus } from '@/bindings/OrderStatus'
import type { QueueOrder } from '@/bindings/QueueOrder'
import type { QueueResponse } from '@/bindings/QueueResponse'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { buttonClass } from '@/components/button-class'
import { FormError } from '@/components/form-error'
import { PrintIcon } from '@/components/icons'
import { Page } from '@/components/layout'
import { backOfficeKeys, branchSettingsQuery, queueQuery } from '@/features/back-office/api'
import { OrderActions } from '@/features/back-office/order-actions'
import { usePrintOne } from '@/features/back-office/print-one'
import { ACTIVE_STATUSES, readQueueFilter, toggleQueueStatus, writeQueueFilter } from '@/features/back-office/queue-filter'
import { useMediaQuery } from '@/lib/media'
import { PrintOnly } from '@/features/back-office/tickets'
import { LiveIndicator, OrderCard } from '@/features/ordering/order-view'
import { cx } from '@/lib/cx'
import { useManagedBranch } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { branchEventsUrl, useBranchEvents } from '@/lib/live'

const ORDER_PATH = '/$restaurantSlug/$branchSlug/manage/orders/$orderId'
/** From here up the queue is one column per status; below, one status at a time. */
const WIDE = '(min-width: 768px)'

interface Scope {
  restaurantSlug: string
  branchSlug: string
  restaurantId: string
  branchId: string
  /** The restaurant's main language: the T4.3 fallback for item names on the tickets. */
  main: Language
  /** The hint stream is up: every move we make comes back as a hint, so it refetches for us. */
  live: boolean
  /** Print one ticket (PRD: READY take-away) as an 80 mm thermal ticket, G14. */
  print: (order: QueueOrder) => void
}

/**
 * The order queue (PRD, G9): the branch's active orders as tickets, and per ticket the
 * one-step "next" and a separated two-press cancel. The page holds every active order and
 * filters on the device, so the picker always counts each status. A phone shows one status
 * at a time; a wide screen shows the chosen statuses side by side, one column each. Every
 * move is the server's answer, never a guess (T8): a stale card is told where the order
 * went and the list refetches. Live through G5's hints, polling behind them. Every role in
 * the branch. A READY take-away ticket prints on its own (G14): the page steps off the paper
 * while the chosen ticket is on it.
 */
export function BranchQueuePage() {
  const t = useT()
  const { restaurantSlug, branchSlug, restaurantId, branchId, context } = useManagedBranch()
  const queryClient = useQueryClient()
  const wide = useMediaQuery(WIDE)
  const [statuses, setStatuses] = useState(readQueueFilter)
  // A phone shows the first chosen status; picking another replaces the choice.
  const shown = wide ? statuses : statuses.slice(0, 1)
  const printing = usePrintOne<QueueOrder>()
  const branch = context?.branches.find((candidate) => candidate.id === branchId)
  // The stream is slug-addressed (G5); a branch the context does not list opens none.
  const live = useBranchEvents(context && branch ? branchEventsUrl(context.settings.slug, branch.slug) : undefined, () => {
    void queryClient.invalidateQueries({ queryKey: backOfficeKeys.queue(restaurantId, branchId) })
    void queryClient.invalidateQueries({ queryKey: backOfficeKeys.branchSettings(restaurantId, branchId) })
  })
  const list = useQuery(queueQuery(restaurantId, branchId))

  const scope: Scope = { restaurantSlug, branchSlug, restaurantId, branchId, main: context.settings.main_language, live: live === 'live', print: printing.print }
  function choose(status: OrderStatus) {
    const next = wide ? toggleQueueStatus(statuses, status) : [status]
    writeQueueFilter(next)
    setStatuses(next)
  }

  return (
    <>
      <Page width="full" className={cx('flex flex-col gap-4', printing.chosen && 'print:hidden')}>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold">{t('queue.title')}</h1>
          {branch && <span className="text-muted">{branch.name}</span>}
          <ShopState scope={scope} />
        </div>
        <LiveIndicator status={live} />
        <div role="group" aria-label={t('queue.filter')} className="flex flex-wrap gap-2">
          {ACTIVE_STATUSES.map((status) => {
            const on = shown.includes(status)
            const count = list.data?.orders.filter((order) => order.status === status).length
            return (
              <button
                key={status}
                type="button"
                aria-pressed={on}
                disabled={wide && on && statuses.length === 1}
                onClick={() => choose(status)}
                className={cx(
                  'inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3 text-sm font-medium select-none',
                  on ? 'bg-accent text-accent-fg border-accent' : 'border-border text-muted hover:bg-accent-soft',
                )}
              >
                {t(`track.status.${status}`)}
                {/* The space keeps the name "Sent 3" for readers; flex drops it from the layout. */}
                {count !== undefined && ' '}
                {count !== undefined && (
                  <span className={cx('rounded-full px-1.5 text-xs tabular-nums', on ? 'bg-accent-fg/20' : 'bg-accent-soft')}>{count}</span>
                )}
              </button>
            )
          })}
        </div>
        <Tickets scope={scope} list={list} statuses={shown} columns={wide} />
      </Page>
      {printing.chosen && (
        <PrintOnly>
          <OrderCard order={printing.chosen} currency={printing.chosen.currency} main={scope.main} code venue={{ restaurant: context.settings.name, branch: branch?.name, address: branch?.address, phone: branch?.phone }} />
        </PrintOnly>
      )}
    </>
  )
}

/** Open/closed from the settings every role may read (G4); the PIN too, for staff to read out. */
function ShopState({ scope }: { scope: Scope }) {
  const t = useT()
  const settings = useQuery(branchSettingsQuery(scope.restaurantId, scope.branchId))
  if (!settings.data) return null
  return (
    <>
      <Badge tone={settings.data.is_open ? 'success' : 'warning'}>{t(settings.data.is_open ? 'queue.shopOpen' : 'queue.shopClosed')}</Badge>
      {settings.data.order_pin && <span className="text-muted text-sm">{t('queue.pin', { pin: settings.data.order_pin })}</span>}
    </>
  )
}

interface TicketsProps {
  scope: Scope
  list: UseQueryResult<QueueResponse>
  statuses: readonly OrderStatus[]
  /** One column per status side by side; otherwise a single list. */
  columns: boolean
}

function Tickets({ scope, list, statuses, columns }: TicketsProps) {
  const t = useT()
  const queryClient = useQueryClient()

  // The server's answer replaces the ticket at once (T8: never a guess); without the stream, the list is refetched behind it.
  function onMoved(moved: QueueOrder) {
    queryClient.setQueryData<QueueResponse>(backOfficeKeys.queue(scope.restaurantId, scope.branchId), (current) =>
      current && {
        ...current,
        orders: current.orders.map((order) => (order.id === moved.id ? moved : order)).filter((order) => ACTIVE_STATUSES.includes(order.status)),
      },
    )
    if (!scope.live) void queryClient.invalidateQueries({ queryKey: backOfficeKeys.queue(scope.restaurantId, scope.branchId) })
  }

  if (list.isPending) return <p className="text-muted text-sm">{t('common.loading')}</p>
  if (!list.data) {
    return (
      <div className="flex flex-col gap-2">
        <FormError error={list.error} />
        <Button variant="secondary" onClick={() => void list.refetch()} className="self-start">
          {t('common.retry')}
        </Button>
      </div>
    )
  }
  const orders = list.data.orders
  const column = (status: OrderStatus) => {
    const mine = orders.filter((order) => order.status === status)
    if (mine.length === 0) return <p className="text-muted text-sm">{t('queue.empty')}</p>
    return (
      <ul className="flex flex-col gap-3">
        {mine.map((order) => (
          // Keyed by status too: a ticket that moved under us drops its stale-move notice.
          <li key={`${order.id}:${order.status}`}>
            <Ticket scope={scope} order={order} onMoved={onMoved} />
          </li>
        ))}
      </ul>
    )
  }
  if (!columns) return column(statuses[0]!)
  return (
    <div className="grid auto-cols-[minmax(18rem,1fr)] grid-flow-col gap-4 overflow-x-auto pb-2">
      {statuses.map((status) => (
        <section key={status} aria-label={t(`track.status.${status}`)} className="flex min-w-0 flex-col gap-3">
          <h2 className="text-muted text-sm font-semibold uppercase">{t(`track.status.${status}`)}</h2>
          {column(status)}
        </section>
      ))}
    </div>
  )
}

function Ticket({ scope, order, onMoved }: { scope: Scope; order: QueueOrder; onMoved: (moved: QueueOrder) => void }) {
  const t = useT()
  const queryClient = useQueryClient()
  const params = { restaurantSlug: scope.restaurantSlug, branchSlug: scope.branchSlug, orderId: order.id }

  return (
    <OrderCard
      order={order}
      currency={order.currency}
      main={scope.main}
      code
      title={
        <Link to={ORDER_PATH} params={params} className="hover:underline">
          {t('track.title', { number: order.number })}
        </Link>
      }
      actions={
        <OrderActions
          restaurantId={scope.restaurantId}
          branchId={scope.branchId}
          order={order}
          onMoved={onMoved}
          // The card was stale: let the refetch redraw it where the order is.
          onStale={() => void queryClient.invalidateQueries({ queryKey: backOfficeKeys.queue(scope.restaurantId, scope.branchId) })}
          middle={
            <span className="flex items-center gap-2">
              {/* G10: the details page for returns, real weight, print. */}
              <Link to={ORDER_PATH} params={params} className={buttonClass('secondary', 'sm')}>
                {t('queue.details')}
              </Link>
              {/* PRD: the ticket goes with the food; dine-in prints as one session bill instead. */}
              {order.order_type === 'take_away' && order.status === 'ready' && (
                <Button size="sm" variant="secondary" onClick={() => scope.print(order)}>
                  <PrintIcon className="size-4" />
                  {t('orderDetails.print')}
                </Button>
              )}
            </span>
          }
        />
      }
    />
  )
}
